export interface StayTransaction {
  visitDate: string | null;
  visitTime: string | null;
  accountDate: string;
  accountTime: string | null;
  customerCount: number | null;
  stayDurationSec: number | null;
}

export interface OccupancyPoint {
  day: string;
  occupied: number | null;
  stayMinutes: number | null;
  occupancyRate: number | null;
  completedGroups: number;
}

export interface OccupancyDay {
  points: OccupancyPoint[];
  transactionCount: number;
  validOccupancyCount: number;
  validStayCount: number;
  reliableOccupancy: boolean;
  reliableStay: boolean;
  customers: number;
  averageStayMinutes: number | null;
  peakOccupied: number | null;
  peakOccupancyRate: number | null;
}

const timestamp = (date: string | null, time: string | null): number | null => {
  if (!date || !time || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{1,2}:\d{2}(:\d{2})?$/.test(time)) return null;
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute, second = 0] = time.split(':').map(Number);
  if (hour > 23 || minute > 59 || second > 59) return null;
  return Date.UTC(year, month - 1, day, hour, minute, second);
};

const hourLabel = (hour: number): string => `${String(hour).padStart(2, '0')}:00`;
const MIN_COVERAGE = 0.8;
const hourStart = (date: string, hour: number): number => Date.parse(`${date}T00:00:00Z`) + hour * 3_600_000;

/** 伝票作成～会計を在席期間とみなし、1時間に重なった人・秒を人数へ換算する。 */
export const calculateOccupancyDay = (
  transactions: StayTransaction[],
  date: string,
  startHour: number,
  endHour: number,
  seatCount: number | null,
  dayChangeTime = '05:00',
): OccupancyDay => {
  const daily = transactions.filter((transaction) => airRegiBusinessDate(transaction, dayChangeTime) === date);
  const validStay = daily.filter((transaction) =>
    transaction.stayDurationSec != null && transaction.stayDurationSec > 0);
  const intervals = daily.flatMap((transaction) => {
    const start = timestamp(transaction.visitDate, transaction.visitTime);
    const end = timestamp(transaction.accountDate, transaction.accountTime);
    const count = Number(transaction.customerCount);
    if (start == null || end == null || end <= start || !Number.isInteger(count) || count <= 0
      || !transaction.stayDurationSec || transaction.stayDurationSec <= 0) return [];
    return [{ start, end, count }];
  });
  const reliableOccupancy = daily.length > 0 && intervals.length / daily.length >= MIN_COVERAGE;
  const reliableStay = daily.length > 0 && validStay.length / daily.length >= MIN_COVERAGE;
  const hasSeats = seatCount != null && seatCount > 0;
  const points = Array.from({ length: Math.max(0, endHour - startHour) }, (_, index): OccupancyPoint => {
    const hour = startHour + index;
    const from = hourStart(date, hour);
    const to = from + 3_600_000;
    const occupied = !reliableOccupancy ? null
      : intervals.reduce((sum, interval) =>
        sum + Math.max(0, Math.min(interval.end, to) - Math.max(interval.start, from)) / 3_600_000 * interval.count, 0);
    const completed = validStay.filter((transaction) =>
      airRegiAccountHour(transaction, dayChangeTime) === hour);
    const stayMinutes = reliableStay && completed.length
      ? completed.reduce((sum, transaction) => sum + transaction.stayDurationSec!, 0) / completed.length / 60
      : null;
    return {
      day: hourLabel(hour), occupied, stayMinutes,
      occupancyRate: occupied != null && hasSeats ? occupied / seatCount * 100 : null,
      completedGroups: completed.length,
    };
  });
  const peakOccupied = reliableOccupancy ? Math.max(...points.map((point) => point.occupied ?? 0)) : null;
  return {
    points,
    transactionCount: daily.length,
    validOccupancyCount: intervals.length,
    validStayCount: validStay.length,
    reliableOccupancy,
    reliableStay,
    customers: daily.reduce((sum, transaction) => sum + Math.max(0, Number(transaction.customerCount) || 0), 0),
    averageStayMinutes: reliableStay
      ? validStay.reduce((sum, transaction) => sum + transaction.stayDurationSec!, 0) / validStay.length / 60
      : null,
    peakOccupied,
    peakOccupancyRate: peakOccupied != null && hasSeats ? peakOccupied / seatCount * 100 : null,
  };
};
import { airRegiAccountHour, airRegiBusinessDate } from './airRegiBusinessTime';
